import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlignLeft, FileText, Languages, ListChecks, Network, Pause, Play } from 'lucide-react';

type TabId = 'transcript' | 'summary' | 'mindmap' | 'keypoints' | 'translation';

const TABS: { id: TabId; icon: React.ElementType; tint: string; labelKey: string }[] = [
  { id: 'transcript', icon: AlignLeft, tint: 'var(--tint-transcript)', labelKey: 'transcription.tabs.transcript' },
  { id: 'summary', icon: FileText, tint: 'var(--tint-summary)', labelKey: 'transcription.tabs.summary' },
  { id: 'mindmap', icon: Network, tint: 'var(--tint-mindmap)', labelKey: 'transcription.tabs.mindmap' },
  { id: 'keypoints', icon: ListChecks, tint: 'var(--tint-keypoints)', labelKey: 'transcription.tabs.keypoints' },
  { id: 'translation', icon: Languages, tint: 'var(--tint-translate)', labelKey: 'transcription.tabs.translation' },
];

const SPEAKER_COLORS = ['#007aff', '#af52de', '#34a853'];

// Sample content is demo data, not UI copy, so it lives here rather than in every locale file.
const SAMPLE = {
  en: {
    file: 'Weekly product sync.m4a',
    meta: '32:14 · 3 speakers · English',
    speakers: ['Maya', 'Daniel', 'Priya'],
    segments: [
      { s: 0, t: '00:00', text: "Okay, let's get started. The big item today is the launch date for the new onboarding flow." },
      { s: 1, t: '00:09', text: 'Design is done. Engineering needs about one more week for the mobile edge cases.' },
      { s: 2, t: '00:18', text: "Then let's target the 14th and keep the beta small — maybe two hundred users." },
      { s: 0, t: '00:27', text: 'Agreed. Daniel, can you share the launch checklist with support by Friday?' },
      { s: 1, t: '00:34', text: "Yes, I'll send it tomorrow morning." },
    ],
    summaryTitle: 'Launch plan for the new onboarding flow',
    summaryLead: 'The team aligned on a launch date and a small beta before rolling out to everyone.',
    summarySections: [
      { h: 'Decisions', items: ['Launch targeted for the 14th', 'Beta limited to ~200 users'] },
      { h: 'Action items', items: ['Daniel → share launch checklist with support (Fri)', 'Engineering → finish mobile edge cases'] },
    ],
    mind: {
      root: 'Onboarding launch',
      branches: [
        { label: 'Timeline', leaves: ['Design complete', '+1 week engineering', 'Target: the 14th'] },
        { label: 'Beta', leaves: ['~200 users', 'Small, then expand'] },
        { label: 'Actions', leaves: ['Checklist → support', 'Due Friday'] },
      ],
    },
    keypoints: [
      { q: 'When is the launch?', a: 'The 14th, after one more week of engineering work.', t: '00:18' },
      { q: 'Who gets early access?', a: 'A small beta group of about two hundred users.', t: '00:18' },
      { q: "What's the next step?", a: 'Daniel shares the launch checklist with support by Friday.', t: '00:27' },
    ],
    translationPair: 'English → 简体中文',
    translations: [
      '好，我们开始吧。今天最重要的是确定新版引导流程的上线日期。',
      '设计已经完成，工程还需要大约一周处理移动端的边界情况。',
      '那我们定在 14 号，先小范围内测，大概两百个用户。',
      '同意。Daniel，周五前能把上线检查清单发给客服团队吗？',
      '可以，我明天上午发出。',
    ],
  },
  zh: {
    file: '每周产品例会.m4a',
    meta: '32:14 · 3 位说话人 · 中文',
    speakers: ['林雨', '陈浩', '王珊'],
    segments: [
      { s: 0, t: '00:00', text: '好，我们开始吧。今天最重要的是确定新版引导流程的上线日期。' },
      { s: 1, t: '00:09', text: '设计已经完成，工程还需要大约一周处理移动端的边界情况。' },
      { s: 2, t: '00:18', text: '那我们定在 14 号，先小范围内测，大概两百个用户。' },
      { s: 0, t: '00:27', text: '同意。陈浩，周五前能把上线检查清单发给客服团队吗？' },
      { s: 1, t: '00:34', text: '可以，我明天上午发出。' },
    ],
    summaryTitle: '新版引导流程上线计划',
    summaryLead: '团队确定了上线日期，并决定在全量发布前先进行小范围内测。',
    summarySections: [
      { h: '决定', items: ['上线日期定为 14 号', '内测规模约 200 名用户'] },
      { h: '待办事项', items: ['陈浩 → 周五前把检查清单发给客服', '工程 → 完成移动端边界情况'] },
    ],
    mind: {
      root: '引导流程上线',
      branches: [
        { label: '时间线', leaves: ['设计已完成', '工程再需一周', '目标：14 号'] },
        { label: '内测', leaves: ['约 200 名用户', '先小范围再扩大'] },
        { label: '行动', leaves: ['检查清单 → 客服', '周五截止'] },
      ],
    },
    keypoints: [
      { q: '什么时候上线？', a: '14 号，工程还需要一周时间。', t: '00:18' },
      { q: '谁能提前体验？', a: '约两百名用户的小范围内测。', t: '00:18' },
      { q: '下一步是什么？', a: '陈浩周五前把上线检查清单发给客服。', t: '00:27' },
    ],
    translationPair: '简体中文 → English',
    translations: [
      "Okay, let's get started. The big item today is the launch date for the new onboarding flow.",
      'Design is done. Engineering needs about one more week for the mobile edge cases.',
      "Then let's target the 14th and keep the beta small — about two hundred users.",
      'Agreed. Chen Hao, can you share the launch checklist with support by Friday?',
      "Sure, I'll send it tomorrow morning.",
    ],
  },
};

const WAVE = Array.from({ length: 64 }, (_, i) => {
  const v = Math.abs(Math.sin(i * 0.55) * 0.6 + Math.sin(i * 1.7) * 0.3 + Math.sin(i * 0.13) * 0.25);
  return 0.18 + Math.min(v, 1) * 0.82;
});

const SpeakerDot: React.FC<{ index: number; name: string }> = ({ index, name }) => (
  <span
    className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-semibold text-white"
    style={{ background: SPEAKER_COLORS[index % SPEAKER_COLORS.length] }}
    aria-hidden="true"
  >
    {name.slice(0, 1)}
  </span>
);

const MindMap: React.FC<{ data: (typeof SAMPLE)['en']['mind'] }> = ({ data }) => {
  const colors = ['rgb(var(--tint-transcript))', 'rgb(var(--tint-summary))', 'rgb(var(--tint-keypoints))'];
  const branchY = [62, 160, 252];
  const root = { x: 20, y: 160, w: 150 };
  return (
    <svg viewBox="0 0 660 320" className="h-auto w-full" role="img" aria-label={data.root}>
      {data.branches.map((branch, bi) => {
        const by = branchY[bi];
        const leafStart = by - ((branch.leaves.length - 1) * 34) / 2;
        return (
          <g key={branch.label}>
            <path
              d={`M ${root.x + root.w} ${root.y} C ${root.x + root.w + 50} ${root.y}, 210 ${by}, 250 ${by}`}
              fill="none"
              style={{ stroke: colors[bi] }}
              strokeOpacity="0.55"
              strokeWidth="2"
            />
            {branch.leaves.map((leaf, li) => {
              const ly = leafStart + li * 34;
              return (
                <g key={leaf}>
                  <path d={`M 360 ${by} C 395 ${by}, 400 ${ly}, 430 ${ly}`} fill="none" style={{ stroke: colors[bi] }} strokeOpacity="0.35" strokeWidth="1.5" />
                  <circle cx="430" cy={ly} r="3" style={{ fill: colors[bi] }} />
                  <text x="442" y={ly + 4.5} fontSize="13" style={{ fill: 'rgb(var(--ink-2))' }}>{leaf}</text>
                </g>
              );
            })}
            <rect x="250" y={by - 16} width="110" height="32" rx="16" style={{ fill: colors[bi] }} fillOpacity="0.14" />
            <text x="305" y={by + 5} textAnchor="middle" fontSize="13.5" fontWeight="600" style={{ fill: colors[bi] }}>{branch.label}</text>
          </g>
        );
      })}
      <rect x={root.x} y={root.y - 20} width={root.w} height="40" rx="20" style={{ fill: 'rgb(var(--ink))' }} />
      <text x={root.x + root.w / 2} y={root.y + 5} textAnchor="middle" fontSize="14" fontWeight="600" style={{ fill: 'rgb(var(--bg))' }}>{data.root}</text>
    </svg>
  );
};

const ProductDemo: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
  const sample = lang.startsWith('zh') ? SAMPLE.zh : SAMPLE.en;
  const [tab, setTab] = useState<TabId>('transcript');
  const [autoplay, setAutoplay] = useState(true);
  const [activeSegment, setActiveSegment] = useState(0);
  const [playing, setPlaying] = useState(true);

  const reducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    []
  );

  useEffect(() => {
    if (!playing || reducedMotion) return;
    const id = window.setInterval(() => {
      setActiveSegment((i) => (i + 1) % sample.segments.length);
    }, 2200);
    return () => window.clearInterval(id);
  }, [playing, reducedMotion, sample.segments.length]);

  useEffect(() => {
    if (!autoplay || reducedMotion) return;
    const id = window.setTimeout(() => {
      const idx = TABS.findIndex((x) => x.id === tab);
      setTab(TABS[(idx + 1) % TABS.length].id);
    }, tab === 'transcript' ? 9000 : 4500);
    return () => window.clearTimeout(id);
  }, [autoplay, reducedMotion, tab]);

  const selectTab = (id: TabId) => {
    setAutoplay(false);
    setTab(id);
  };

  const activeTint = TABS.find((x) => x.id === tab)!.tint;
  const progress = (activeSegment + 0.5) / sample.segments.length;

  return (
    <div
      className="overflow-hidden rounded-[18px] bg-raised text-start shadow-window"
      onPointerDown={() => setAutoplay(false)}
    >
      {/* Title bar */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex gap-2" aria-hidden="true">
          <i className="h-3 w-3 rounded-full bg-[#ff5f57]" />
          <i className="h-3 w-3 rounded-full bg-[#febc2e]" />
          <i className="h-3 w-3 rounded-full bg-[#28c840]" />
        </div>
        <div
          role="tablist"
          aria-label="OmniScribe"
          className="mx-auto flex max-w-full items-center gap-0.5 overflow-x-auto rounded-full bg-card p-1 [scrollbar-width:none]"
        >
          {TABS.map(({ id, icon: Icon, tint, labelKey }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => selectTab(id)}
              className="tab-pill"
              style={{ ['--pill-tint' as string]: tint }}
              title={t(labelKey)}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="max-sm:sr-only">{t(labelKey)}</span>
            </button>
          ))}
        </div>
        <span className="w-[52px] max-sm:hidden" />
      </div>

      <div className="grid gap-4 p-4 pt-1.5 sm:p-5 sm:pt-2 lg:grid-cols-[260px_1fr]">
        {/* Media column */}
        <div className="flex flex-col gap-3 rounded-[16px] bg-card p-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[11px] bg-gradient-to-b from-[#ff8a3d] to-[#f05a28] text-white">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true"><path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 1 0 6 0V6a3 3 0 0 0-3-3Zm-7 9a1 1 0 1 1 2 0 5 5 0 0 0 10 0 1 1 0 1 1 2 0 7 7 0 0 1-6 6.93V21a1 1 0 1 1-2 0v-2.07A7 7 0 0 1 5 12Z" /></svg>
            </span>
            <div className="min-w-0">
              <div className="truncate text-[14px] font-semibold text-ink">{sample.file}</div>
              <div className="truncate text-[12px] text-ink-2">{sample.meta}</div>
            </div>
          </div>

          <div className="flex h-12 items-center gap-[2px]" aria-hidden="true">
            {WAVE.map((h, i) => (
              <span
                key={i}
                className="flex-1 rounded-full transition-colors duration-500"
                style={{
                  height: `${h * 100}%`,
                  background: i / WAVE.length < progress ? 'rgb(var(--accent))' : 'rgb(var(--ink-3) / 0.35)',
                }}
              />
            ))}
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className="grid h-9 w-9 place-items-center rounded-full bg-ink text-bg transition-transform active:scale-95"
              aria-label={playing ? 'Pause' : 'Play'}
            >
              {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="ms-0.5 h-4 w-4" fill="currentColor" />}
            </button>
            <span className="tnum font-rounded text-[13px] font-semibold text-ink-2">
              {sample.segments[activeSegment].t} <span className="text-ink-3">/ 32:14</span>
            </span>
          </div>

          <div className="mt-1 space-y-1.5 border-t border-line pt-3 max-lg:hidden">
            {sample.speakers.map((name, i) => (
              <div key={name} className="flex items-center gap-2.5 text-[13px] text-ink-2">
                <SpeakerDot index={i} name={name} />
                <span className="flex-1 text-ink">{name}</span>
                <span className="tnum text-[12px] text-ink-3">{[42, 35, 23][i]}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div
          key={tab}
          className="tab-fade-slide min-h-[340px] rounded-[16px] bg-card p-4 sm:p-5"
          style={{ ['--pill-tint' as string]: activeTint }}
          role="tabpanel"
        >
          {tab === 'transcript' && (
            <ol className="space-y-1">
              {sample.segments.map((seg, i) => (
                <li
                  key={i}
                  className={`flex gap-3 rounded-[12px] px-3 py-2.5 transition-colors duration-300 ${
                    i === activeSegment ? 'bg-raised shadow-[0_1px_2px_rgba(0,0,0,0.05)]' : ''
                  }`}
                >
                  <SpeakerDot index={seg.s} name={sample.speakers[seg.s]} />
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[13px] font-semibold text-ink">{sample.speakers[seg.s]}</span>
                      <span className="tnum text-[12px] text-ink-3">{seg.t}</span>
                    </div>
                    <p className={`mt-0.5 text-[14.5px] leading-relaxed ${i === activeSegment ? 'text-ink' : 'text-ink-2'}`}>
                      {seg.text}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}

          {tab === 'summary' && (
            <div className="max-w-xl">
              <span className="chip" style={{ ['--chip-tint' as string]: 'var(--tint-summary)' }}>AI</span>
              <h4 className="mt-3 text-[19px] font-semibold tracking-[-0.015em] text-ink">{sample.summaryTitle}</h4>
              <p className="mt-2 text-[14.5px] leading-relaxed text-ink-2">{sample.summaryLead}</p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {sample.summarySections.map((sec) => (
                  <div key={sec.h} className="rounded-[14px] bg-raised p-4">
                    <div className="text-[12.5px] font-semibold text-tint-summary">{sec.h}</div>
                    <ul className="mt-2 space-y-1.5">
                      {sec.items.map((item) => (
                        <li key={item} className="flex gap-2 text-[14px] leading-snug text-ink">
                          <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-tint-summary" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'mindmap' && (
            <div className="flex h-full items-center">
              <MindMap data={sample.mind} />
            </div>
          )}

          {tab === 'keypoints' && (
            <ol className="space-y-2.5">
              {sample.keypoints.map((kp, i) => (
                <li key={kp.q} className="flex gap-3.5 rounded-[14px] bg-raised p-4">
                  <span className="num !text-[13px]" style={{ ['--section-tint' as string]: 'var(--tint-keypoints)' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[14.5px] font-semibold text-ink">{kp.q}</div>
                    <p className="mt-1 text-[14px] leading-relaxed text-ink-2">{kp.a}</p>
                  </div>
                  <span className="chip h-fit" style={{ ['--chip-tint' as string]: 'var(--tint-keypoints)' }}>{kp.t}</span>
                </li>
              ))}
            </ol>
          )}

          {tab === 'translation' && (
            <div>
              <span className="chip" style={{ ['--chip-tint' as string]: 'var(--tint-translate)' }}>{sample.translationPair}</span>
              <div className="mt-4 divide-y divide-line">
                {sample.segments.slice(0, 4).map((seg, i) => (
                  <div key={i} className="grid gap-1.5 py-3 sm:grid-cols-2 sm:gap-6">
                    <p className="text-[14px] leading-relaxed text-ink-2">{seg.text}</p>
                    <p className="text-[14px] leading-relaxed text-ink">{sample.translations[i]}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDemo;
